import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/sr';
import eng from './locales/eng.json';
import srb from './locales/srb.json';
import { DEFAULT_UI_LANGUAGE, I18N_LANGUAGE_CODES, isUiLanguage, UiLanguage } from './language.config';
import { DAYJS_LOCALES } from './locales';

/**
 * The language picked from the sidebar lives in sessionStorage: it survives a reload, and
 * is gone when the tab closes. Deliberately not localStorage and not the user's account -
 * the language is a setting of the deployment, and a choice made in the menu is a
 * temporary override of it, not a preference that follows anyone around.
 *
 * Storage can throw (private windows, blocked site data), so every access is guarded and
 * the panel simply opens in the default language when it does.
 */
const SESSION_KEY = 'veerny-admin-language';

const readSessionLanguage = (): UiLanguage | null => {
  try {
    const value = sessionStorage.getItem(SESSION_KEY);
    return isUiLanguage(value) ? value : null;
  } catch {
    return null;
  }
};

const applyToDocument = (language: UiLanguage) => {
  // dayjs formats month and weekday names, and the date pickers read it through their adapter
  dayjs.locale(DAYJS_LOCALES[language]);
  document.documentElement.lang = I18N_LANGUAGE_CODES[language];
};

const initialLanguage = readSessionLanguage() ?? DEFAULT_UI_LANGUAGE;

i18n.use(initReactI18next).init({
  resources: {
    [I18N_LANGUAGE_CODES.srb]: { translation: srb },
    [I18N_LANGUAGE_CODES.eng]: { translation: eng }
  },
  lng: I18N_LANGUAGE_CODES[initialLanguage],
  fallbackLng: I18N_LANGUAGE_CODES[DEFAULT_UI_LANGUAGE],
  interpolation: {
    // React already escapes what it renders; escaping here would show &amp; on screen
    escapeValue: false
  },
  returnNull: false
});

applyToDocument(initialLanguage);

export const setUiLanguage = async (language: UiLanguage): Promise<void> => {
  try {
    sessionStorage.setItem(SESSION_KEY, language);
  } catch {
    // the switch still applies to this page; it just will not survive a reload
  }
  applyToDocument(language);
  await i18n.changeLanguage(I18N_LANGUAGE_CODES[language]);
};

export default i18n;
